<script>
    import { onMount } from 'svelte';
    import pb from '../lib/pocketbase';

    export let newEntity = { impuestos: [] };
    export let entityName = "impuesto";
    export let canModify = false;
    export let canCreate = false;
    export let currentEntityId = null;

    let editMode = {}; // Track which impuesto is in edit mode
    let updatedValues = {}; // Track updates during edit
    let availableImpuestos = [];
    let selectedImpuestoId = null;
    let validationErrors = {}; // To store validation errors for each impuesto

    // Initialize updatedValues with the same structure as newEntity.impuestos and add tasa and porc
    const fetchImpuestos = async () => {
        try {
            const res = await pb.collection('impuesto_plantilla').getFullList({
                filter: `sta=true`,
            });

            // Map the fetched data to availableImpuestos
            availableImpuestos = res.map(item => ({
                id: item.id,
                descrip: item.descrip
            }));

            // Initialize updatedValues with each newEntity.impuestos entry and add tasa and porc
            updatedValues = newEntity.impuestos.reduce((acc, { impuesto }) => {
                acc[impuesto.id] = {
                    tasa: impuesto.tasa || 12,  // Default to 12 if not provided
                    porc: impuesto.porc ?? false,  // Default to false if not provided
                };
                return acc;
            }, {});

            console.log('updatedValues', updatedValues);
        } catch (error) {
            console.error('Error al cargar impuestos:', error);
        }
    };

    // Enable editing for a specific impuesto
    const enableEdit = (id) => {
        editMode = { ...editMode, [id]: true };
        validationErrors[id] = ''; // Clear any previous validation errors
    };

    // Save the edited impuesto
    const saveEdit = (id) => {
        const index = newEntity.impuestos.findIndex(attr => attr.impuesto.id === id);
        if (index !== -1) {
            const impuesto = newEntity.impuestos[index].impuesto;

            // Validation: Ensure tasa and porcentaje are not empty
            if (impuesto.requerido && (updatedValues[id]?.tasa === '' || updatedValues[id]?.porc === undefined)) {
                validationErrors[id] = 'Todos los campos son obligatorios.';
                return;
            }

            // Update the impuesto in the array
            newEntity.impuestos[index] = {
                impuesto: {
                    ...impuesto,
                    tasa: updatedValues[id]?.tasa || 0,  // Default to 0 if not provided
                    porc: updatedValues[id]?.porc ?? false,  // Default to false if not provided
                }
            };

            // Reset edit mode and updatedValues for the current impuesto
            editMode = { ...editMode, [id]: false };
            updatedValues = { ...updatedValues, [id]: null };  // Clear the updated values

            // Optionally, update the state with a fresh list
            newEntity = { ...newEntity, impuestos: [...newEntity.impuestos] };

            // Optionally clear validation errors
            delete validationErrors[id];
        }
    };

    // Discard the changes made while editing
    const discardEdit = (id) => {
        editMode = { ...editMode, [id]: false };
        updatedValues[id] = null;
    };

    // Handle input changes in the form
    const handleInputChange = (id, field, value) => {
        updatedValues = {
            ...updatedValues,
            [id]: { ...updatedValues[id], [field]: value },
        };
        if (validationErrors[id]) validationErrors[id] = ''; // Clear error if input changes
    };

    // Create a new impuesto if one is selected
    const crearImpuesto = () => {
        if (selectedImpuestoId) {
            const impuestoSeleccionado = availableImpuestos.find(attr => attr.id === selectedImpuestoId);
            const nuevoImpuesto = {
                impuesto: {
                    id: impuestoSeleccionado.id,
                    descrip: impuestoSeleccionado.descrip,
                    tasa: 0,
                    porc: false
                },
            };

            if (!newEntity.impuestos.some(attr => attr.impuesto.id === nuevoImpuesto.impuesto.id)) {
                newEntity.impuestos = [...newEntity.impuestos, nuevoImpuesto];
                selectedImpuestoId = null;
            }
        }
    };

    // Remove an impuesto from the list
    const eliminarImpuesto = (id) => {
        newEntity.impuestos = newEntity.impuestos.filter(attr => attr.impuesto.id !== id);
        delete validationErrors[id]; // Remove validation error if exists
    };

    onMount(() => {
        fetchImpuestos();
        console.log('newEntity.impuestos', newEntity.impuestos);
    });
</script>

<div class="text-gray-600">
    <h3 class="text-lg font-bold mb-4">Impuestos</h3>

    <!-- Select and Create New Impuesto -->
    <div class="mb-4">
        <select bind:value={selectedImpuestoId} class="border p-1 mr-2" disabled={!canModify && (!canCreate || currentEntityId)}>
            <option value="" selected>Seleccionar impuesto</option>
            {#each availableImpuestos as { id, descrip }}
                {#if !newEntity.impuestos.some(attr => attr.impuesto.id === id)}
                    <option value={id}>{descrip}</option>
                {/if}
            {/each}
        </select>

        {#if canModify || canCreate}
            <button class="bg-lightBlue-500 text-white px-4 py-2 rounded" on:click={crearImpuesto}>
                Crear
            </button>
        {/if}
    </div>

    <!-- Display Impuestos in Table -->
    {#if newEntity.impuestos.length > 0}
        <table class="items-center w-full bg-transparent border-collapse">
            <thead>
                <tr class="bg-gray-100">
                    <th class="px-6 py-3 text-xs uppercase border font-semibold">Descripción</th>
                    <th class="px-6 py-3 text-xs uppercase border font-semibold">Tasa</th>
                    <th class="px-6 py-3 text-xs uppercase border font-semibold">Porcentaje</th>
                    <th class="px-6 py-3 text-xs uppercase border font-semibold">Acciones</th>
                </tr>
            </thead>
            <tbody>
                {#each newEntity.impuestos as { impuesto }}
                    <tr>
                        <td class="px-6 py-3 text-xs">{impuesto.descrip}</td>
                        
                        <td class="px-6 py-3 text-xs">
                            {#if editMode[impuesto.id]}
                                <input
                                    type="number"
                                    class="border border-gray-300 rounded p-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    value={updatedValues[impuesto.id]?.tasa || impuesto.tasa || 0}
                                    on:input={(e) => handleInputChange(impuesto.id, 'tasa', parseFloat(e.target.value) || 0)}
                                    disabled={!canModify && (!canCreate || currentEntityId)}
                                />
                            {:else}
                                <span>{impuesto.tasa || '0'}</span>
                            {/if}
                        </td>
                        
                        <td class="px-6 py-3 text-xs">
                            {#if editMode[impuesto.id]}
                                <input
                                    type="checkbox"
                                    checked={updatedValues[impuesto.id]?.porc ?? impuesto.porc}
                                    on:change={(e) => handleInputChange(impuesto.id, 'porc', e.target.checked)}
                                />
                            {:else}
                                <span>{impuesto.porc ? 'Sí' : 'No'}</span>
                            {/if}
                        </td>
                        
                        <td class="px-6 py-3 text-xs">
                            {#if canModify || canCreate}
                                {#if editMode[impuesto.id]}
                                    <button on:click={() => saveEdit(impuesto.id)} class="bg-blueGray-600 text-white px-2 py-1 rounded">Guardar</button>
                                    <button on:click={() => discardEdit(impuesto.id)} class="bg-red-400 text-white px-2 py-1 rounded">Descartar</button>
                                {:else}
                                    <button on:click={() => enableEdit(impuesto.id)} class="bg-yellow-500 text-white px-2 py-1 rounded">Editar</button>
                                    <button on:click={() => eliminarImpuesto(impuesto.id)} class="bg-red-500 text-white px-2 py-1 rounded">Eliminar</button>
                                {/if}
                            {/if}
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    {/if}
</div>